<template>
  <div>
    <loading-indicator v-if="isLoading"/>

    <p v-if="message !== null">
      {{ message }}
    </p>

    <template v-if="isDerbyVisible">
      <table class="derby-table prop-table mb-4">
        <tr>
          <th>Аудитория</th>
          <td>
            {{ derby.audience | getDerbyAudienceNameVerbose }}
          </td>
        </tr>
        <tr class="min-player-level">
          <th>
            Минимальный<br>уровень игрока
          </th>
          <td>
            {{ derby.min_player_level | getFormattedNumber }}
          </td>
        </tr>
        <tr class="min-client-version">
          <th>
            Минимальная<br>версия клиента
          </th>
          <td>
            {{ derby.min_client_version | convertOptionalVersionCodeToStr('не указана') }}
          </td>
        </tr>
        <tr>
          <th>Статус</th>
          <td :class="getDerbyStatusClass(derby.status)">
            {{ derby.status | getStatusName }}
          </td>
        </tr>
        <tr>
          <th>
            <span v-b-popover.hover.top="'Дата и время, когда пользователь инсайдера создал скачки.'">
              Создание
            </span>
          </th>
          <td>
            {{ derby.created_at | getMomentMinStr }}
          </td>
        </tr>
        <tr>
          <th>Начало</th>
          <td>
            {{ derby.start_at | getMomentMinStr }}
          </td>
        </tr>
        <tr class="min-playing">
          <th>
            <span v-b-popover.hover.top="'В течение указанного периода перед завершением скачек присоединение новых игроков запрещено.'">
              Минимальная<br>длительность участия
            </span>
          </th>
          <td>
            {{ minPlayingDurationStr }}
          </td>
        </tr>
        <tr class="join-period-end">
          <th>
            <span v-b-popover.hover.top.html="'Из-за минимальной длительности участия игрока новые игроки не&nbsp;могут присоединиться к скачкам после указанного времени.'">
              Конец присоединения<br>новых участников
            </span>
          </th>
          <td>
            {{ joinPeriodEnd | getMomentMinStr }}
          </td>
        </tr>
        <tr>
          <th>Конец</th>
          <td>
            {{ derby.finish_at | getMomentMinStr }}
          </td>
        </tr>
      </table>

      <DerbyABTest
        v-if="hasABTest"
        :abTest="abTest"
      />
    </template>

  </div>
</template>

<script>
import { HTTP } from '../../http-common.js';
import notifier from '../../notifier.js';

/**
 * @typedef { import('../../model/race.js').DerbyABTest } DerbyABTest
 */
import { Derby                       } from '../../model/race.js';
import { getDerbyStatusClass         } from '../../model/race.js';
import { getDerbyAudienceNameVerbose } from '../../model/race.js';

import { convertDurationSecToRuStr } from '../../util.js';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

import LoadingIndicator from '../LoadingIndicator.vue';
import DerbyABTest from './DerbyABTest.vue';

export default {
  name: 'DerbyInfo',

  components: {
    LoadingIndicator,
    DerbyABTest,
  },

  props: {
    derbyId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      loadingStatus: LOADING_STATUS__INIT,

      /** @type {?Object} */
      derby: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.derby === null) {
        return `Скачки с идентификатором ${this.derbyId} не существуют.`;
      }
      return null;
    },

    /** @returns {boolean} */
    isDerbyVisible() {
      return this.isLoadedNormal && (this.derby !== null);
    },

    /** @returns {string} */
    minPlayingDurationStr() {
      return convertDurationSecToRuStr(this.derby.prohibition_interval);
    },

    /**
     * Возвращает момент времени в секундах. 
     * @returns {number}
     */
    joinPeriodEnd() {
      const derby = this.derby;
      return derby.finish_at - derby.prohibition_interval;
    },

    /** @returns {boolean} */
    hasABTest() {
      return this.abTest !== null;
    },

    /** @returns {?DerbyABTest} */
    abTest() {
      const derby = this.derby;
      if(derby === null) {
        return null;
      }

      const id    = derby.ab_test_id;
      const group = derby.ab_test_group;

      if(id === 0 && group.length === 0) {
        return null;
      }

      return {
        id:    id,
        group: group,
      };
    },
  },

  created() {
    this.loadDerby();
  },

  methods: {
    loadDerby() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = `/derby/${this.derbyId}/info`;

      HTTP.get(url)
      .then(response => {
        this.derby = response.data.data;
        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    getDerbyStatusClass,
  },

  filters: {
    getStatusName: Derby.getStatusName,
    getDerbyAudienceNameVerbose,
  },
}
</script>

<style scoped>
.derby-table > tr > td {
  vertical-align: top;
}

.min-player-level,
.min-client-version,
.min-playing,
.join-period-end {
  line-height: 1.15;
}
</style>
